import { useState, type FormEvent } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router";
import { api, errorMessage, isCode } from "../api/client.ts";
import { isDemo, isMock } from "../api/mode.ts";
import type { Me } from "../api/types.ts";
import { CodeInput } from "../components/CodeInput.tsx";
import { IconArrow } from "../components/Icons.tsx";
import { Alert, Spinner } from "../components/ui.tsx";
import { rich, useT } from "../i18n/index.tsx";
import { useSession } from "../lib/session.tsx";
import { AuthLayout } from "./AuthLayout.tsx";

/** Only allow same-site paths as a return target. */
const safeNext = (v: string | null) => (v && v.startsWith("/") && !v.startsWith("//") && !v.startsWith("/login") ? v : "/");

export function Login() {
  const t = useT();
  const { me, setMe } = useSession();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const next = safeNext(params.get("next"));
  const [step, setStep] = useState<"password" | "mfa">("password");
  const [email, setEmail] = useState(() => params.get("email") ?? "");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ tone: "error" | "warn"; text: string } | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);

  if (me) return <Navigate to={next} replace />;

  const submitPassword = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      setError({ tone: "error", text: t.auth.enterEmailPassword });
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ step: string } | Me>("/auth/login", { email: email.trim(), password });
      if ("user" in r) {
        // Demo build only: signed in without a two-step code.
        setMe(r);
        navigate(next, { replace: true });
        return;
      }
      setStep("mfa");
      setCode("");
    } catch (err) {
      if (isCode(err, "invalid_credentials")) setError({ tone: "error", text: t.common.errors.invalid_credentials });
      else if (isCode(err, "rate_limited", "ip_not_allowed", "no_access")) setError({ tone: "warn", text: errorMessage(err) });
      else setError({ tone: "error", text: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (value = code) => {
    if (value.length !== 6) {
      setCodeError(t.auth.enterCode);
      return;
    }
    setBusy(true);
    setCodeError(null);
    setError(null);
    try {
      const m = await api.post<Me>("/auth/mfa", { code: value });
      setMe(m);
      navigate(next, { replace: true });
    } catch (err) {
      setBusy(false);
      setCode("");
      if (isCode(err, "mfa_required", "invalid_credentials")) setCodeError(t.auth.codeRejectedLogin);
      else if (isCode(err, "unauthenticated")) {
        setStep("password");
        setError({ tone: "warn", text: t.auth.signInTimedOut });
      } else if (isCode(err, "rate_limited", "ip_not_allowed", "no_access")) setError({ tone: "warn", text: errorMessage(err) });
      else setError({ tone: "error", text: errorMessage(err) });
    }
  };

  return (
    <AuthLayout>
      {step === "password" ? (
        <form className="auth-form" onSubmit={submitPassword} noValidate>
          <h1>{t.auth.logIn}</h1>
          <p className="lead-sm">{t.auth.loginLead}</p>
          {error && <Alert tone={error.tone}>{error.text}</Alert>}
          <div className="field">
            <label htmlFor="email">{t.auth.workEmail}</label>
            <input id="email" type="email" autoComplete="username" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus required />
          </div>
          <div className="field">
            <label htmlFor="password">{t.auth.password}</label>
            <div className="input-affix">
              <input id="password" type={showPw ? "text" : "password"} autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              <button type="button" className="affix-btn" onClick={() => setShowPw(!showPw)} aria-pressed={showPw} aria-label={showPw ? t.auth.hidePassword : t.auth.showPassword}>
                {showPw ? t.auth.hide : t.auth.show}
              </button>
            </div>
          </div>
          <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
            {busy ? <Spinner /> : null} {t.common.continue} {!busy && <IconArrow size={16} />}
          </button>
          <p className="auth-note">
            {rich(t.auth.forgot, { a: (c) => <a href="https://icryptonow.com/contact/" target="_blank" rel="noreferrer noopener">{c}</a> })}
          </p>
          {isMock && (
            <p className="mock-hint">{isDemo ? t.auth.demoHint : rich(t.auth.mockHint)}</p>
          )}
        </form>
      ) : (
        <form className="auth-form" onSubmit={(e) => { e.preventDefault(); void submitCode(); }} noValidate>
          <div className="eyebrow auth-eyebrow">{t.auth.mfaEyebrow}</div>
          <h1>{t.auth.mfaTitle}</h1>
          <p className="lead-sm">{rich(t.auth.mfaLead, {}, { email: email.trim() })}</p>
          {error && <Alert tone={error.tone}>{error.text}</Alert>}
          <CodeInput value={code} onChange={(v) => { setCode(v); setCodeError(null); }} onComplete={(v) => void submitCode(v)} error={codeError} disabled={busy} autoFocus />
          <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
            {busy ? <Spinner /> : null} {t.auth.verifyAndLogIn}
          </button>
          <button type="button" className="link-btn" onClick={() => { setStep("password"); setError(null); setCodeError(null); setPassword(""); }}>
            {t.auth.useDifferentAccount}
          </button>
        </form>
      )}
    </AuthLayout>
  );
}
